import { clone, cloneDeep, isArray, isEmpty, toPairs } from 'lodash'
import Vue from 'vue'

import { SHARED_NAMESPACE } from '@/store/shared/index'

import { axiosRequest } from '@/plugins/axios-common'
import { handleError } from '@/store/handle-error'
import { partyName } from '@/utilities'

export const TOOL_MODE = {
  copy: 'COPY',
  merge: 'MERGE',
}

const EMPTY_STATE = {
  selectedSourceParty: null,
  selectedTargetParty: null,
  sourceParty: {},
  targetParty: {},
  sourcePartyPhoto: null,
  targetPartyPhoto: null,
  partyDiff: [],
  partyMergeInfo: {},
  copiedFields: {},
  copiedRelations: {},
  didSelectAllFields: {},
  showOnlyDiff: false,
  showOnlyCopied: false,
  toolMode: TOOL_MODE.copy,
  deleteSourceParty: false,
  fields: [
    {
      merge: false,
      source: {
        label: null,
        key: null,
        value: null,
        valueText: null,
      },
      target: {
        label: null,
        key: null,
        value: null,
        valueText: null,
      },
    },
  ],
  utils: {
    panel: null,
  },
}

export const PartyMergeState = {
  mergeParty: cloneDeep(EMPTY_STATE),
}

const MERGE_PARTY_REMOVES = [
  'id',
  'ext_id',
  'destroyable?',
]

export const PartyMergeActions = {
  getMergePartyDetailed ({ commit, dispatch }, requestData) {
    if (requestData.id === null) {
      if (requestData.type === 'source') commit('setMergePartySource', {})
      else if (requestData.type === 'target') commit('setMergePartyTarget', {})
      return
    }

    return axiosRequest.
      get(`/parties/${requestData.id}?detailed=true`).
      then(({ data }) => {
        const party = data.party || {}

        for (const key of Object.keys(party)) if (!isEmpty(party[key]) && !isArray(party[key])) party[key] = [party[key]]

        if (requestData.type === 'source') commit('setMergePartySource', party)
        else if (requestData.type === 'target') commit('setMergePartyTarget', party)

        dispatch('processPartyDiff')
        dispatch('processPartyPhotos')
      }).
      catch(handleError)
  },
  async doMergeParty ({ commit, getters }) {
    const sourceParty = getters.mergePartySource
    const targetParty = getters.mergePartyTarget
    const deleteSource = getters.mergePartyToolMode === TOOL_MODE.merge
    const fields = toPairs(getters.mergePartyCopiedFields).filter(f => f[1]).map(f => f[0])
    const relations = toPairs(getters.mergePartyCopiedRelations).reduce((p, c) => {
      p[c[0]] = c[1].map(rel => rel.id)
      return p
    }, {})

    const requestData = {
      source: {
        id: sourceParty.id,
        delete: deleteSource,
        fields: fields,
        relations: relations,
      },
      target: { id: targetParty.id },
    }

    try {
      await axiosRequest.post(`/parties/${targetParty.id}/merge`, { party: requestData })
    } catch (e) {
      return handleError(e)
    }
  },
  async fetchPartyMergeInfo ({ commit }) {
    try {
      const { data } = await axiosRequest.get('/parties/merge_info')
      return commit('setMergePartyMergeInfo', data)
    } catch (e) {
      return handleError(e)
    }
  },
  processPartyDiff ({ commit, getters }) {
    const sourceParty = getters.mergePartySource
    const targetParty = getters.mergePartyTarget

    if (
      isEmpty(sourceParty) ||
      isEmpty(targetParty)
    ) return

    const diff = getters.mergePartyNativeProperties.
      filter(({ name }) => targetParty[name] !== sourceParty[name])
    commit('setMergePartyDiff', diff.map(({ name }) => name))
  },
  async processPartyPhotos ({ commit, getters, dispatch }) {
    const sourceParty = getters.mergePartySource
    const targetParty = getters.mergePartyTarget

    let photo = null
    if (sourceParty?.photo) {
      photo = await dispatch(`${SHARED_NAMESPACE}/getThumbnailForAttachment`,
        sourceParty.photo.id,
        { root: true }
      )
      commit('setMergePartySourcePhoto', URL.createObjectURL(photo))
    } else commit('setMergePartySourcePhoto', null)

    if (targetParty?.photo) {
      photo = await dispatch(`${SHARED_NAMESPACE}/getThumbnailForAttachment`,
        targetParty.photo.id,
        { root: true }
      )
      commit('setMergePartyTargetPhoto', URL.createObjectURL(photo))
    } else commit('setMergePartyTargetPhoto', null)
  },
  toggleMergePartyProperty ({ commit, getters }, property) {
    const copiedFields = getters.mergePartyCopiedFields

    if (copiedFields[property]) commit('unsetMergePartyCopiedFieldProperty', property)
    else commit('setMergePartyCopiedFieldProperty', property)
  },
  toggleMergePartyRelation ({ commit, getters }, { property, relation }) {
    const copiedRelations = getters.mergePartyCopiedRelations[property]
    if (!copiedRelations) {
      commit('setMergePartyCopiedFieldRelationship', {
        key: property,
        relations: [relation],
      })
    } else {
      const relationIndex = copiedRelations.findIndex(r => r.id === relation.id)
      if (relationIndex !== -1) copiedRelations.splice(relationIndex, 1)
      else copiedRelations.push(relation)

      commit('setMergePartyCopiedFieldRelationship', {
        key: property,
        relations: copiedRelations,
      })
    }
  },
  resetMergeParty ({ commit, state }) {
    const newState = cloneDeep(EMPTY_STATE)
    newState.partyMergeInfo = state.mergeParty.partyMergeInfo
    commit('setMergePartyState', newState)
  },
  resetSelections ({ commit }) {
    commit('setDidSelectAllFields', {})
    commit('setMergePartyAllCopiedFields', {})
    commit('setMergePartyAllCopiedRelations', {})
  },
  selectAllFromSource ({ commit, getters, dispatch }) {
    const sourceParty = getters.mergePartySource
    const relations = getters.mergePartyRelations

    const copiableRelations = relations.filter(relation => sourceParty[relation.name])

    dispatch('selectAllFieldsFromSource')
    for (const relation of copiableRelations) dispatch('selectAllFromRelationFromSource', relation)
  },
  deselectAllFromSource ({ commit, getters, dispatch }) {
    const sourceParty = getters.mergePartySource
    const relations = getters.mergePartyRelations

    const copiableRelations = relations.filter(relation => sourceParty[relation.name])

    dispatch('deselectAllFieldsFromSource')
    for (const relation of copiableRelations) dispatch('deselectAllFromRelationFromSource', relation)
  },
  selectAllFieldsFromSource ({ commit, getters }) {
    const diffFields = getters.mergePartyDiffs
    const fields = getters.mergePartyNativeProperties

    const copiedFields = fields.reduce((obj, field) => {
      if (diffFields.includes(field.name)) obj[field.name] = true
      return obj
    }, {})

    copiedFields.photo = true // support for party photo

    commit('setMergePartyAllCopiedFields', copiedFields)
    commit('setDidSelectAllFieldState', { key: 'nativeProperties', value: true })
  },
  deselectAllFieldsFromSource ({ commit }) {
    commit('setMergePartyAllCopiedFields', {})
    commit('setDidSelectAllFieldState', { key: 'nativeProperties', value: false })
  },
  selectAllFromRelationFromSource ({ commit, getters }, relation) {
    const sourceParty = getters.mergePartySource
    commit('setMergePartyCopiedFieldRelationship', { key: relation.name, relations: cloneDeep(sourceParty[relation.name]) })
    commit('setDidSelectAllFieldState', { key: relation.name, value: true })
  },
  deselectAllFromRelationFromSource ({ commit }, relation) {
    commit('setMergePartyCopiedFieldRelationship', { key: relation.name, relations: [] })
    commit('setDidSelectAllFieldState', { key: relation.name, value: false })
  },
}

export const PartyMergeMutations = {
  setMergePartySource: (state, party) => { state.mergeParty.sourceParty = party },
  setMergePartyTarget: (state, party) => { state.mergeParty.targetParty = party },
  setMergePartySourcePhoto: (state, photo) => { state.mergeParty.sourcePartyPhoto = photo },
  setMergePartyTargetPhoto: (state, photo) => { state.mergeParty.targetPartyPhoto = photo },
  setMergePartyDiff: (state, partyDiff) => { state.mergeParty.partyDiff = partyDiff },
  setMergePartyShowOnlyDiff: (state, payload) => { state.mergeParty.showOnlyDiff = payload },
  setMergePartyShowOnlyCopied: (state, payload) => { state.mergeParty.showOnlyCopied = payload },
  setMergePartyUnionFields: (state, payload) => { state.mergeParty.partyUnionFields = payload },
  setMergePartyMergeInfo: (state, payload) => { state.mergeParty.partyMergeInfo = payload },
  setMergePartyOpenPanel: (state, payload) => { state.mergeParty.utils.panel = payload },
  setMergePartyCopiedFieldProperty: (state, key) => { Vue.set(state.mergeParty.copiedFields, key, true) },
  unsetMergePartyCopiedFieldProperty: (state, key) => { Vue.set(state.mergeParty.copiedFields, key, false) },
  setMergePartyCopiedFieldRelationship: (state, { key, relations }) => { Vue.set(state.mergeParty.copiedRelations, key, relations) },
  setMergePartyToolMode: (state, payload) => { state.mergeParty.toolMode = payload },
  setMergePartyState: (state, payload) => { Vue.set(state, 'mergeParty', clone(payload)) },
  setMergePartyAllCopiedFields: (state, payload) => { Vue.set(state.mergeParty, 'copiedFields', clone(payload)) },
  setMergePartyAllCopiedRelations: (state, payload) => { Vue.set(state.mergeParty, 'copiedRelations', cloneDeep(payload)) },
  setDidSelectAllFieldState: (state, { key, value }) => { Vue.set(state.mergeParty.didSelectAllFields, key, value) },
  setDidSelectAllFields: (state, payload) => { Vue.set(state.mergeParty, 'didSelectAllFields', cloneDeep(payload)) },
}

export const PartyMergeGetters = {
  didSelectAll: state => state.mergeParty.didSelectAllFields,
  mergeParty: state => state.mergeParty,
  mergePartySource: state => state.mergeParty.sourceParty,
  mergePartyTarget: state => state.mergeParty.targetParty,
  mergePartyDiffs: state => state.mergeParty.partyDiff,
  sourceParties: state => {
    const parties = state.parties.map((party, index) => {
      return {
        id: party.id,
        fullName: partyName(party),
      }
    })
    return parties
  },
  targetParties: state => party => {
    const parties = state.parties.filter(x => x.id !== party?.id).map((party, index) => {
      return {
        id: party.id,
        fullName: partyName(party),
      }
    })
    return parties
  },
  mergePartyNativeProperties: state => state.mergeParty.partyMergeInfo?.properties.filter(e => !MERGE_PARTY_REMOVES.includes(e.name)) || [],
  mergePartyRelations: state => state.mergeParty.partyMergeInfo?.relations || [],
  mergePartyOpenPanel: state => state.mergeParty.utils.panel,
  mergePartyCopiedFields: state => state.mergeParty.copiedFields,
  mergePartyCopiedRelations: state => state.mergeParty.copiedRelations,
  mergePartyToolMode: state => state.mergeParty.toolMode,
  mergePartyHasBothParties: state => !isEmpty(state.mergeParty.sourceParty) && !isEmpty(state.mergeParty.targetParty),
  mergePartyRelationIsCopied: state => (relation, id) => state.mergeParty.copiedRelations[relation]?.find(r => r.id === id) !== undefined,
  partyDiff: state => state.mergeParty.partyDiff,
  photoURL: state => state.mergeParty.sourcePartyPhoto,
  showOnlyCopied: state => state.mergeParty.showOnlyCopied,
  showOnlyDiff: state => state.mergeParty.showOnlyDiff,
  sourcePhoto: state => state.mergeParty.sourcePartyPhoto,
  targetPhoto: state => state.mergeParty.targetPartyPhoto,
}
